import { useState, useEffect, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Users,
  Plus,
  Edit,
  Trash2,
  Eye,
  CheckCircle,
  XCircle
} from 'lucide-react'
import { toast } from 'react-hot-toast'
import { merchantService } from '../services/merchantService'
import { DetailHeader } from '@/components/ui/DetailHeader'
import DataTable from '@/components/ui/DataTable'
import FilterBar from '@/components/ui/FilterBar'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Alert, AlertDescription } from '@/components/ui/alert'

interface MerchantUser {
  id: string
  merchantId: string
  merchantName: string
  userId: string
  userEmail: string
  userName: string
  permissionLevel: 'owner' | 'admin' | 'manager' | 'viewer'
  isActive: boolean
  createdAt: string
  updatedAt: string
}

interface LocalMerchant {
  id: string
  name: string
  businessName: string
  businessType: string
}

const MerchantUsersPage = () => {
  const [merchantUsers, setMerchantUsers] = useState<MerchantUser[]>([])
  const [merchants, setMerchants] = useState<LocalMerchant[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [filters, setFilters] = useState<any[]>([])
  const [searchQuery, setSearchQuery] = useState('')

  const navigate = useNavigate()

  useEffect(() => {
    loadData()
  }, [])

  useEffect(() => {
    // Monitoring state updates for debugging
  }, [merchantUsers])

  const loadData = async () => {
    try {
      setLoading(true)
      setError(null)
      
      // Load merchants
      const merchantsResponse = await merchantService.getAll({ limit: 100 })
      const merchantsList: LocalMerchant[] = merchantsResponse.merchants.map(m => ({
        id: m.merchant_id,
        name: m.business_name,
        businessName: m.business_name,
        businessType: m.business_type
      }))
      setMerchants(merchantsList)
      
      // Create a map for quick merchant lookup
      const merchantMap = new Map<string, string>()
      merchantsList.forEach(m => merchantMap.set(m.id, m.businessName))
      
      // Load merchant users for each merchant
      const allMerchantUsers: MerchantUser[] = []
      for (const merchant of merchantsList) {
        try {
          const response = await merchantService.getMerchantUsers(merchant.id)
          
          if (response.users && response.users.length > 0) {
            const merchantUsers: MerchantUser[] = response.users.map((mu: any) => {
              // L'API retourne 'user_email', 'user_first_name', 'user_last_name', 'user_name'
              const userEmail = mu.email || mu.user_email || mu.userEmail || ''
              const firstName = mu.first_name || mu.user_first_name || ''
              const lastName = mu.last_name || mu.user_last_name || ''
              let userName = mu.user_name || mu.userName || ''
              
              // Si userName est vide, essayez de le construire à partir du prénom et nom
              if (!userName.trim()) {
                if (firstName || lastName) {
                  userName = `${firstName} ${lastName}`.trim()
                } else {
                  userName = `User ${mu.user_id?.substring(0, 8) || 'unknown'}`
                }
              }
              
              // Garantir que userName n'est jamais vide
              if (!userName.trim()) {
                userName = 'Utilisateur inconnu'
              }
              
              // Garantir que userEmail n'est jamais vide
              const finalUserEmail = userEmail || 'Email non disponible'
              
              return {
                id: mu.merchant_user_id || mu.id || '',
                merchantId: mu.merchant_id || merchant.id,
                merchantName: merchant.businessName || 'Commerce inconnu',
                userId: mu.user_id || '',
                userEmail: finalUserEmail,
                userName: userName,
                permissionLevel: mu.permission_level || 'viewer',
                isActive: mu.is_active !== undefined ? mu.is_active : false,
                createdAt: mu.created_at || new Date().toISOString(),
                updatedAt: mu.updated_at || new Date().toISOString()
              }
            })
            allMerchantUsers.push(...merchantUsers)
          }
        } catch (error) {
          // Continue with other merchants
        }
      }
      
      setMerchantUsers(allMerchantUsers)
    } catch (error) {
      setError('Erreur lors du chargement des données')
      toast.error('Erreur lors du chargement des données')
    } finally {
      setLoading(false)
    }
  }

  const formatDate = (dateString: string) => {
    const date = new Date(dateString)
    return date.toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    })
  }

  const getPermissionBadge = (level: string) => {
    switch (level) {
      case 'owner':
        return <Badge variant="default" className="bg-purple-100 text-purple-800 border-purple-200">Propriétaire</Badge>
      case 'admin':
        return <Badge variant="default" className="bg-blue-100 text-blue-800 border-blue-200">Administrateur</Badge>
      case 'manager':
        return <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">Gestionnaire</Badge>
      case 'viewer':
        return <Badge variant="outline" className="bg-gray-50 text-gray-700 border-gray-200">Visualiseur</Badge>
      default:
        return <Badge variant="outline">{level}</Badge>
    }
  }

  const getStatusBadge = (isActive: boolean) => {
    return isActive ? (
      <Badge variant="default" className="inline-flex items-center bg-green-100 text-green-800 border-green-200">
        <CheckCircle className="h-3 w-3 mr-1" />
        Actif
      </Badge>
    ) : (
      <Badge variant="destructive" className="inline-flex items-center">
        <XCircle className="h-3 w-3 mr-1" />
        Inactif
      </Badge>
    )
  }

  const columns = useMemo(() => [
    {
      key: 'userName',
      label: 'Utilisateur',
      sortable: true,
      render: (_value: any, row: MerchantUser) => {
        const userName = row?.userName
        const userEmail = row?.userEmail
        return (
          <div className="flex items-center">
            <div className="flex-shrink-0 h-10 w-10">
              <div className="h-10 w-10 rounded-full bg-primary-100 flex items-center justify-center">
                <Users className="h-6 w-6 text-primary-600" />
              </div>
            </div>
            <div className="ml-4">
              <div className="text-sm font-medium text-gray-900">
                {userName || 'Nom inconnu'}
              </div>
              <div className="text-sm text-gray-500">
                {userEmail || 'Aucun email'}
              </div>
            </div>
          </div>
        )
      }
    },
    {
      key: 'merchantName',
      label: 'Commerce',
      sortable: true,
      render: (_value: any, row: MerchantUser) => (
        <div className="text-sm text-gray-900">
          {row?.merchantName || 'Commerce inconnu'}
        </div>
      )
    },
    {
      key: 'permissionLevel',
      label: 'Permission',
      sortable: true,
      render: (_value: any, row: MerchantUser) => getPermissionBadge(row?.permissionLevel || 'viewer')
    },
    {
      key: 'isActive',
      label: 'Statut',
      sortable: true,
      render: (_value: any, row: MerchantUser) => getStatusBadge(row?.isActive || false)
    },
    {
      key: 'createdAt',
      label: 'Créé le',
      sortable: true,
      render: (_value: any, row: MerchantUser) => (
        <div className="text-sm text-gray-900">
          {row?.createdAt ? formatDate(row.createdAt) : 'Date inconnue'}
        </div>
      )
    }
  ], [])

  const actions = useMemo(() => [
    {
      label: 'Voir',
      icon: <Eye className="h-4 w-4" />,
      onClick: (row: MerchantUser) => navigate(`/users/${row.userId}`)
    },
    {
      label: 'Modifier',
      icon: <Edit className="h-4 w-4" />,
      onClick: (_row: MerchantUser) => {
        toast.success('Fonctionnalité de modification à implémenter')
      }
    },
    {
      label: 'Activer/Désactiver',
      icon: <CheckCircle className="h-4 w-4" />,
      onClick: async (row: MerchantUser) => {
        try {
          await merchantService.updateMerchantUser(row.merchantId, row.userId, {
            is_active: !row.isActive
          })
          toast.success(`Utilisateur ${!row.isActive ? 'activé' : 'désactivé'} avec succès`)
          loadData()
        } catch (error) {
          console.error('Error toggling user status:', error)
          toast.error('Erreur lors de la modification du statut')
        }
      }
    },
    {
      label: 'Supprimer',
      icon: <Trash2 className="h-4 w-4" />,
      onClick: async (row: MerchantUser) => {
        if (!window.confirm('Êtes-vous sûr de vouloir supprimer cet utilisateur marchand ?')) {
          return
        }

        try {
          await merchantService.removeMerchantUser(row.merchantId, row.userId)
          toast.success('Utilisateur marchand supprimé avec succès')
          loadData()
        } catch (error) {
          console.error('Error deleting merchant user:', error)
          toast.error('Erreur lors de la suppression')
        }
      }
    }
  ], [navigate])

  const filterOptions = useMemo(() => [
    {
      key: 'merchantId',
      label: 'Commerce',
      type: 'select' as const,
      placeholder: 'Tous les commerces',
      options: [
        { value: 'all', label: 'Tous les commerces' },
        ...merchants.map(merchant => ({ value: merchant.id, label: merchant.businessName }))
      ]
    },
    {
      key: 'permissionLevel',
      label: 'Permission',
      type: 'select' as const,
      placeholder: 'Tous les niveaux',
      options: [
        { value: 'all', label: 'Tous les niveaux' },
        { value: 'owner', label: 'Propriétaire' },
        { value: 'admin', label: 'Administrateur' },
        { value: 'manager', label: 'Gestionnaire' },
        { value: 'viewer', label: 'Visualiseur' }
      ]
    },
    {
      key: 'isActive',
      label: 'Statut',
      type: 'select' as const,
      placeholder: 'Tous les statuts',
      options: [
        { value: 'all', label: 'Tous les statuts' },
        { value: 'active', label: 'Actif' },
        { value: 'inactive', label: 'Inactif' }
      ]
    }
  ], [merchants])

  const handleFilterChange = (newFilters: any[]) => {
    setFilters(newFilters)
  }

  const handleSearch = (value: string) => {
    setSearchQuery(value)
  }

  const filteredData = useMemo(() => {
    let result = merchantUsers

    // Apply search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase()
      result = result.filter(user => {
        const userName = user.userName || ''
        const userEmail = user.userEmail || ''
        const merchantName = user.merchantName || ''
        
        return (
          userName.toLowerCase().includes(query) ||
          userEmail.toLowerCase().includes(query) ||
          merchantName.toLowerCase().includes(query)
        )
      })
    }

    // Apply other filters
    filters.forEach(filter => {
      if (filter.key === 'merchantId' && filter.value !== 'all') {
        result = result.filter(user => user.merchantId === filter.value)
      }
      if (filter.key === 'permissionLevel' && filter.value !== 'all') {
        result = result.filter(user => user.permissionLevel === filter.value)
      }
      if (filter.key === 'isActive' && filter.value !== 'all') {
        const isActive = filter.value === 'active'
        result = result.filter(user => user.isActive === isActive)
      }
    })

    return result
  }, [merchantUsers, searchQuery, filters])

  const breadcrumbs = [
    { label: 'Tableau de bord', href: '/' },
    { label: 'Utilisateurs marchands', href: '/merchant-users' }
  ]

  const headerActions = [
    {
      label: 'Ajouter un utilisateur',
      icon: <Plus className="h-4 w-4" />,
      onClick: () => {
        toast.success('Fonctionnalité d\'ajout à implémenter')
      }
    },
    {
      label: 'Actualiser',
      icon: <CheckCircle className="h-4 w-4" />,
      onClick: () => loadData()
    }
  ]

  return (
    <div className="space-y-6">
      <DetailHeader
        title="Utilisateurs marchands"
        subtitle="Gérez les utilisateurs associés aux commerces"
        breadcrumbs={breadcrumbs}
        actions={headerActions}
      />

      {error && (
        <Alert variant="destructive" className="mb-4">
          <AlertDescription>{error}</AlertDescription>
          <div className="mt-4">
            <Button variant="outline" onClick={() => loadData()}>
              Réessayer
            </Button>
          </div>
        </Alert>
      )}

      <FilterBar
        searchPlaceholder="Rechercher par nom, email ou commerce..."
        filters={filterOptions}
        onSearch={handleSearch}
        onFilterChange={handleFilterChange}
      />

      <DataTable
        columns={columns}
        data={filteredData}
        actions={actions}
        loading={loading}
        emptyMessage="Aucun utilisateur marchand trouvé"
      />
    </div>
  )
}

export default MerchantUsersPage